<script setup lang="ts">
import { ref, useTemplateRef, onMounted, watch, watchEffect } from 'vue';
import originalArtwork from './platform-landing/platform-orbit.svg?raw';
import { platformTools, type PlatformTool, type SpriteRegion } from './login-platform';
import { appOrigin } from './navigation';
import LoginToolPopover from './LoginToolPopover.vue';
import { useMediaQuery } from '@vueuse/core';

const props = defineProps<{ activeTool: PlatformTool | null }>();
const emit = defineEmits<{ highlight: [region: SpriteRegion | null] }>();
const hoveredTool = ref<string | null>(null);
const popoverTool = ref<string | null>(null);
const mobile = useMediaQuery('(max-width: 680px)');
watch(mobile, () => {
  popoverTool.value = null;
});
const artwork = useTemplateRef<HTMLDivElement>('artwork');
const labelRegions = ref<Record<string, SpriteRegion>>({});
onMounted(() => {
  for (const tool of platformTools) {
    const label = artwork.value?.querySelector<SVGGraphicsElement>(`[data-tool="${tool.svgPrefix}"] > g[id*="label"]`);
    if (!label) continue;
    const { x, y, width, height } = label.getBBox();
    labelRegions.value[tool.svgPrefix] = {
      x: (x - 420) / 3000,
      y: (y - 200) / 1680,
      width: width / 3000,
      height: height / 1680,
    };
  }
});
function labelStyle(tool: PlatformTool) {
  const region = labelRegions.value[tool.svgPrefix];
  if (!region) return { visibility: 'hidden' as const };
  return {
    '--entrance-delay': `${entranceDelay(tool) + 240}ms`,
    left: `${(region.x + (tool.svgPrefix === 'compliance' ? region.width / 2 : 0)) * 100}%`,
    top: `${(region.y + region.height / 2) * 100}%`,
  };
}
function entranceDelay(tool: PlatformTool) {
  return 480 + platformTools.indexOf(tool) * 140;
}
watchEffect(() => {
  artwork.value?.querySelectorAll<SVGGElement>('[data-tool]').forEach((group) => {
    const key = group.getAttribute('data-tool');
    group.classList.toggle('is-active', key === props.activeTool?.svgPrefix);
    group.classList.toggle('is-hovered', key === hoveredTool.value);
  });
});
function popoverSet(tool: PlatformTool, open: boolean) {
  if (open) popoverTool.value = tool.svgPrefix;
  else if (popoverTool.value === tool.svgPrefix) popoverTool.value = null;
}

watch(popoverTool, hoverTool);

function hoverTool(key: string | null) {
  hoveredTool.value = key;
  const icon = key ? artwork.value?.querySelector<SVGGraphicsElement>(`[data-tool="${key}"] > g[id*="icon"]`) : null;
  if (!icon) {
    emit('highlight', null);
    return;
  }
  const { x, y, width, height } = icon.getBBox();
  emit('highlight', { x: (x - 420) / 3000, y: (y - 200) / 1680, width: width / 3000, height: height / 1680 });
}

// This is a checked-in SVG from the supplied reference, never user-provided markup.
// Keep the SVG mounted during hover so entrance animations only play on arrival.
const dangerousArtwork = originalArtwork
  .replaceAll('#081848', 'var(--platform-background)')
  .replace(/<g data-tool="([^"]+)"/g, (group, key: string) => {
    const tool = platformTools.find((tool) => tool.svgPrefix === key)!;
    const [x, y, width, height] = tool.bounds;
    return `${group} class="orbit-tool" style="--entrance-delay:${entranceDelay(tool)}ms;--flight-x:${(x + width / 2 - 1872.5) * 0.65}px;--flight-y:${(y + height / 2 - 1115.167) * 0.65}px"`;
  });

function toolStyle(tool: PlatformTool) {
  const [x, y, width, height] = tool.bounds;
  return {
    left: `${((x - 420 - 18) / 3000) * 100}%`,
    top: `${((y - 200 - 18) / 1680) * 100}%`,
    width: `${((width + 36) / 3000) * 100}%`,
    height: `${((height + 36) / 1680) * 100}%`,
  };
}
</script>

<template>
  <nav class="login-orbit" aria-label="Toolkit tools">
    <div class="orbit-scene">
      <div
        ref="artwork"
        class="orbit-artwork"
        :class="{ 'has-popover': popoverTool }"
        v-html="dangerousArtwork"
        aria-hidden="true"
      ></div>
      <template v-for="tool in platformTools" :key="tool.svgPrefix">
        <span
          class="orbit-label"
          :class="{
            'is-active': activeTool?.svgPrefix === tool.svgPrefix,
            'is-centered': tool.svgPrefix === 'compliance',
            'is-muted': popoverTool && popoverTool !== tool.svgPrefix,
          }"
          :style="labelStyle(tool)"
          data-sprite-exclusion
        >
          {{ tool.label }}
          <span v-if="tool.svgPrefix === 'consulting'" class="tool-tag">Service</span>
          <span v-else-if="!tool.available" class="tool-tag">Coming soon</span>
        </span>
        <LoginToolPopover
          :tool="tool"
          :active="activeTool?.svgPrefix === tool.svgPrefix"
          :open="!mobile && popoverTool === tool.svgPrefix"
          :mobile="false"
          @update:open="popoverSet(tool, $event)"
        >
          <a
            v-if="tool.available"
            class="orbit-link"
            :class="{ 'is-active': activeTool?.svgPrefix === tool.svgPrefix }"
            :style="toolStyle(tool)"
            :href="activeTool?.svgPrefix === tool.svgPrefix ? '#sign-in' : `${appOrigin(tool.app)}/login`"
            :aria-label="
              activeTool?.svgPrefix === tool.svgPrefix ? `Sign in to ${tool.label}` : `Go to ${tool.label} login`
            "
            :aria-current="activeTool?.svgPrefix === tool.svgPrefix ? 'page' : undefined"
          ></a>
          <button
            v-else
            type="button"
            class="orbit-link"
            :style="toolStyle(tool)"
            :aria-label="`About ${tool.label}`"
          ></button>
        </LoginToolPopover>
      </template>
    </div>

    <div class="orbit-mobile">
      <template v-for="tool in platformTools" :key="tool.svgPrefix">
        <LoginToolPopover
          :tool="tool"
          :active="activeTool?.svgPrefix === tool.svgPrefix"
          :open="mobile && popoverTool === tool.svgPrefix"
          :mobile="true"
          @update:open="popoverSet(tool, $event)"
        >
          <button
            type="button"
            class="mobile-tool"
            :style="{ '--entrance-delay': `${entranceDelay(tool)}ms` }"
            :class="{ 'is-active': activeTool?.svgPrefix === tool.svgPrefix, 'is-unavailable': !tool.available }"
            :aria-label="`About ${tool.label}`"
          >
            <img :src="tool.iconUrl" alt="" />
            <span>{{ tool.label }}</span>
            <span v-if="tool.svgPrefix === 'consulting'" class="tool-tag">Service</span>
            <span v-else-if="!tool.available" class="tool-tag">Soon</span>
          </button>
        </LoginToolPopover>
      </template>
    </div>
  </nav>
</template>

<style scoped>
.login-orbit {
  min-width: 0;
}
.orbit-scene {
  position: relative;
  container-type: inline-size;
}
.orbit-artwork {
  position: relative;
}
.orbit-artwork :deep(svg) {
  display: block;
  width: 100%;
  height: auto;
  overflow: visible;
}
.orbit-artwork :deep(#Orbit_rings) {
  transform-box: fill-box;
  transform-origin: center;
  animation: orbit-arrive 1.6s cubic-bezier(0.16, 1, 0.3, 1) 180ms backwards;
}
.orbit-artwork :deep(#Nodes) {
  animation: orbit-nodes-arrive 1.2s ease-out 450ms backwards;
}
.orbit-artwork :deep(#Stars) {
  animation: orbit-dust-arrive 1.4s ease-out 200ms backwards;
}
.orbit-artwork :deep([data-tool] > g[id*='icon']) {
  transform-box: fill-box;
  transform-origin: center;
  animation: tool-arrive 1.15s cubic-bezier(0.16, 1, 0.3, 1) var(--entrance-delay) backwards;
}
.orbit-artwork :deep(#Nodes) {
  opacity: 0.55;
}
.orbit-artwork :deep(#Nodes circle),
.orbit-artwork :deep(#Nodes path) {
  fill: #5f6a94 !important;
}
.orbit-artwork :deep(.orbit-tool) {
  transition:
    opacity 180ms ease,
    filter 180ms ease;
}
.orbit-artwork.has-popover :deep(.orbit-tool:not(.is-hovered)) {
  opacity: 0.35;
}
.orbit-label.is-muted {
  opacity: 0.35;
}
.orbit-artwork :deep([data-tool] > g[id*='label']) {
  visibility: hidden;
}
.orbit-artwork :deep(#Knockouts > g[id*='label']) {
  display: none;
}
.orbit-artwork :deep(.is-active),
.orbit-artwork :deep(.is-hovered) {
  opacity: 1;
  filter: drop-shadow(0 0 22px rgb(255 196 0 / 32%));
}
.orbit-label {
  position: absolute;
  transform: translateY(-50%);
  color: #f2f5ff;
  font-size: 2.7cqw;
  font-weight: 700;
  line-height: 1.15;
  white-space: nowrap;
  pointer-events: none;
  animation: label-arrive 750ms cubic-bezier(0.16, 1, 0.3, 1) var(--entrance-delay) backwards;
}
@keyframes orbit-arrive {
  from {
    opacity: 0;
    transform: scale(0.2) rotate(-24deg);
    filter: blur(12px);
  }
  70% {
    opacity: 1;
    transform: scale(1.04) rotate(2deg);
    filter: blur(0);
  }
  to {
    opacity: 1;
    transform: none;
    filter: none;
  }
}
@keyframes orbit-nodes-arrive {
  from {
    opacity: 0;
  }
  to {
    opacity: 0.55;
  }
}
@keyframes orbit-dust-arrive {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
@keyframes tool-arrive {
  from {
    opacity: 0;
    transform: translate(var(--flight-x), var(--flight-y)) scale(0.1) rotate(-40deg);
  }
  65% {
    opacity: 1;
    transform: scale(1.18) rotate(6deg);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
@keyframes label-arrive {
  from {
    opacity: 0;
    translate: 0 30px;
    filter: blur(8px);
  }
  to {
    opacity: 1;
    translate: 0 0;
    filter: none;
  }
}
.orbit-label.is-centered {
  transform: translate(-50%, -50%);
}
.orbit-label.is-active {
  color: #ffc400;
}
.tool-tag {
  display: block;
  width: fit-content;
  margin-top: 5px;
  padding: 2px 6px;
  border: 1px dashed #94a8ce;
  border-radius: 4px;
  color: #c3ccea;
  font-size: max(10px, 1.2cqw);
  font-weight: 400;
}
.orbit-link {
  position: absolute;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}
.orbit-link:focus-visible {
  outline: 2px solid #ffc400;
  outline-offset: 4px;
  border-radius: 12px;
}
.orbit-mobile {
  display: none;
}
@media (max-width: 680px) {
  .orbit-scene {
    display: none;
  }
  .orbit-mobile {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-color: #506080 transparent;
    scrollbar-width: thin;
    padding: 4px 2px 10px;
  }
  .mobile-tool {
    display: flex;
    flex: 0 0 92px;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 8px 2px;
    border: 1px solid transparent;
    border-radius: 12px;
    color: #c3ccea;
    font-size: 12px;
    font-family: inherit;
    background: transparent;
    cursor: pointer;
    line-height: 1.2;
    text-align: center;
    text-decoration: none;
    animation: label-arrive 850ms cubic-bezier(0.16, 1, 0.3, 1) var(--entrance-delay) backwards;
  }
  .mobile-tool img {
    width: 44px;
    height: 44px;
  }
  .mobile-tool.is-active {
    color: #ffc400;
    background: rgb(255 196 0 / 5%);
  }
  .mobile-tool.is-active img {
    filter: drop-shadow(0 0 8px rgb(255 196 0 / 35%));
  }
  .mobile-tool:focus-visible {
    outline: 2px solid #ffc400;
    outline-offset: 2px;
  }
  .mobile-tool .tool-tag {
    font-size: 10px;
    margin-top: 0;
  }
}
@media (prefers-reduced-motion: reduce) {
  .orbit-label,
  .mobile-tool,
  .orbit-artwork :deep(#Orbit_rings),
  .orbit-artwork :deep(#Nodes),
  .orbit-artwork :deep(#Stars),
  .orbit-artwork :deep([data-tool] > g[id*='icon']) {
    animation: none;
  }
  .orbit-artwork :deep(.orbit-tool) {
    transition: none;
  }
}
</style>
